import { useId } from 'react';
import { FieldValues, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import type { ZodType, ZodTypeDef } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormProps } from './types';

// When validation fails, the errored field can be scrolled off-screen (above the CTA on mobile, or
// further up a long form on desktop), leaving the customer with no feedback. Bring the topmost invalid
// field into view so they land on the first thing to fix; scrolling down reveals any others naturally.
// If that field is already visible we leave the scroll position alone.
const scrollToFirstError = () => {
  if (typeof window === 'undefined') return;

  // Defer to the next frame so the failing fields have rendered their aria-invalid state first.
  window.requestAnimationFrame(() => {
    if (typeof document === 'undefined') return;
    const invalidFields = Array.from(document.querySelectorAll<HTMLElement>('[aria-invalid="true"]'));
    if (!invalidFields.length) return;

    const topmostField = invalidFields.reduce((closest, field) =>
      field.getBoundingClientRect().top < closest.getBoundingClientRect().top ? field : closest,
    );

    const { top, bottom } = topmostField.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isAlreadyInView = top >= 0 && bottom <= viewportHeight;
    if (isAlreadyInView) return;

    topmostField.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
};

function Form<
  TFieldValues extends FieldValues = FieldValues,
  Schema extends ZodType<TFieldValues, ZodTypeDef, TFieldValues> = ZodType<TFieldValues, ZodTypeDef, TFieldValues>,
>({ onSubmit, defaultValues, children, options, schema }: FormProps<TFieldValues, Schema>) {
  const formId = `form${useId()}`;
  const methods = useForm<TFieldValues>({
    mode: 'all',
    defaultValues,
    resolver: schema && zodResolver(schema),
    shouldUnregister: true,
    ...options,
  });

  const formSubmit: SubmitHandler<TFieldValues> = args => {
    onSubmit(args, methods);
  };

  const onInvalidSubmit: SubmitErrorHandler<TFieldValues> = () => {
    scrollToFirstError();
  };

  return (
    <form onSubmit={methods.handleSubmit(formSubmit, onInvalidSubmit)} id={formId} name={formId}>
      {children(methods)}
    </form>
  );
}

export default Form;
